import React, { createContext, useContext, useState, useEffect } from 'react';
import { getDefaultDateRange } from '../utils/dateUtils';

const OverviewFilterContext = createContext();

export const useOverviewFilters = () => {
  const context = useContext(OverviewFilterContext);
  if (!context) {
    throw new Error('useOverviewFilters must be used within an OverviewFilterProvider');
  }
  return context;
};

export const OverviewFilterProvider = ({ children }) => {
  const [appliedFilters, setAppliedFilters] = useState({
    websites: [],
    contracts: [],
    sessions: [],
    userIdentities: [],
    dateRange: getDefaultDateRange()
  });

  // Get teamId from selectedTeamData (which has _id) or fallback to selectedTeam name
  const [teamId, setTeamId] = useState(() => {
    try {
      const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
      return selectedTeamData._id || localStorage.getItem('selectedTeam') || '';
    } catch (e) {
      return localStorage.getItem('selectedTeam') || '';
    }
  });

  // Update teamId when it changes in localStorage
  useEffect(() => {
    const handleTeamChange = () => {
      let newTeamId;
      try {
        const selectedTeamData = JSON.parse(localStorage.getItem('selectedTeamData') || '{}');
        newTeamId = selectedTeamData._id || localStorage.getItem('selectedTeam') || '';
      } catch (e) {
        newTeamId = localStorage.getItem('selectedTeam') || '';
      }
      
      if (newTeamId !== teamId) {
        setTeamId(newTeamId);
        // Reset filters when team changes
        setAppliedFilters({
          websites: [],
          contracts: [],
          sessions: [],
          userIdentities: [],
          dateRange: getDefaultDateRange()
        });
      }
    };

    // Listen for team changes
    window.addEventListener('teamDataUpdated', handleTeamChange);
    
    return () => {
      window.removeEventListener('teamDataUpdated', handleTeamChange);
    };
  }, [teamId]);

  const updateFilters = (newFilters) => {
    setAppliedFilters(newFilters);
  };

  const resetFilters = () => {
    setAppliedFilters({
      websites: [],
      contracts: [],
      sessions: [],
      userIdentities: [],
      dateRange: getDefaultDateRange()
    });
  };

  const hasActiveFilters = () => {
    return appliedFilters.websites.length > 0 ||
           appliedFilters.contracts.length > 0;
  };

  const value = {
    appliedFilters,
    updateFilters,
    resetFilters,
    hasActiveFilters,
    teamId
  };

  return (
    <OverviewFilterContext.Provider value={value}>
      {children}
    </OverviewFilterContext.Provider>
  );
};

export default OverviewFilterContext;
